import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { matchesTargetGroups } from './TargetGroupFilter'
import styles from './style.module.scss'
import { formatDateTime as formatDateTimeInVienna } from '../../../../shared/datetime'

/*
 * Listendarstellung der Vorlagen des aktiven Registers. Zeigt je Vorlage Titel,
 * Betreff (nur E-Mail), Zielgruppe, Status und den Auslöser. Gefiltert wird
 * clientseitig — die Liste ist mit ~30 Einträgen je Register überschaubar.
 *
 * Suchtext und Zielgruppe kommen von aussen: Sie stehen in der Kopfleiste bzw.
 * in der Filterleiste, die von rechts hereinfaehrt, und die gehoeren der Seite
 * und nicht der Liste — sonst waeren sie beim Registerwechsel wieder weg.
 */
function TargetGroups({ groups }) {
	return (
		<span className={styles.targetGroups}>
			{groups.length === 0 ? (
				<span className={styles.targetGroupMuted}>—</span>
			) : (
				groups.map(g => (
					<span key={g} className={styles.targetGroup}>
						{g}
					</span>
				))
			)}
		</span>
	)
}

TargetGroups.propTypes = {
	groups: PropTypes.arrayOf(PropTypes.string).isRequired
}

const formatDate = value => formatDateTimeInVienna(value)

export default function TemplateList({ channel, items, search, targetGroups, onOpen }) {
	const { t } = useTranslation()

	const isEmail = channel === 'email'

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase()
		return items.filter(item => {
			if (!matchesTargetGroups(item, targetGroups)) return false
			if (!q) return true
			return [item.title, item.subject, item.trigger, item.key]
				.filter(Boolean)
				.some(field => field.toLowerCase().includes(q))
		})
	}, [items, search, targetGroups])

	return (
		<div className={styles.listWrap}>
			<div className={styles.listToolbar}>
				<p className={styles.counter}>
					{filtered.length} {t('Templates')}
				</p>
			</div>

			<div className={styles.cards}>
				{filtered.map(item => (
					<button type="button" key={item.key} className={styles.card} onClick={() => onOpen(item.key)}>
						<div className={styles.cardHead}>
							<span className={styles.cardTitle}>{item.title}</span>
							<span className={item.isActive ? styles.badgeActive : styles.badgeInactive}>
								{item.isActive ? t('Active') : t('Inactive')}
							</span>
						</div>

						{isEmail && item.subject && (
							<p className={styles.cardSubject}>
								<span className={styles.cardLabel}>{t('Subject')}:</span> {item.subject}
							</p>
						)}

						<p className={styles.cardTrigger}>{item.trigger}</p>

						<div className={styles.cardMeta}>
							<TargetGroups groups={item.targetGroups} />
							<span className={styles.cardPlaceholders}>
								{item.placeholderCount} {t('Placeholders')}
							</span>
							{item.isEdited && (
								<span className={styles.cardEdited}>
									{t('Edited')}
									{item.updatedByName ? ` · ${item.updatedByName}` : ''}
									{item.updatedAt ? ` · ${formatDate(item.updatedAt)}` : ''}
								</span>
							)}
						</div>
					</button>
				))}
			</div>
		</div>
	)
}

TemplateList.propTypes = {
	channel: PropTypes.string.isRequired,
	items: PropTypes.array.isRequired,
	search: PropTypes.string,
	targetGroups: PropTypes.array,
	onOpen: PropTypes.func.isRequired
}

TemplateList.defaultProps = {
	search: '',
	targetGroups: []
}
